import { supabase } from "@/config/supabase";
import { Review, ReviewFormData, ReviewStatus } from "@/types";
import { SUPABASE_TABLES } from "@/config/constants";

const REVIEW_HOMEPAGE_COLUMNS = "id,customerName,rating,title,content,createdAt,order";
const REVIEW_STATS_COLUMNS = "id,status,rating";
const REVIEW_COLUMNS_FULL =
  "id,bookingId,customerId,customerName,customerEmail,rating,title,content,serviceCategory,status,approvedBy,approvedAt,rejectedReason,featured,displayOnHomepage,order,googleReviewId,googleReviewUrl,createdAt,updatedAt";

const convertTimestamps = (data: any): any => {
  const converted = { ...data };
  if (converted.createdAt?.toDate) {
    converted.createdAt = converted.createdAt.toDate();
  }
  if (converted.updatedAt?.toDate) {
    converted.updatedAt = converted.updatedAt.toDate();
  }
  if (converted.approvedAt?.toDate) {
    converted.approvedAt = converted.approvedAt.toDate();
  }
  return converted;
};

// Create a new review (customer submission)
export const createReview = async (
  reviewData: ReviewFormData,
  customerId: string,
  customerName: string,
  customerEmail: string
): Promise<string> => {
  const now = new Date();
  const review: Omit<Review, "id"> = {
    ...reviewData,
    customerId,
    customerName,
    customerEmail,
    status: "pending",
    featured: false,
    displayOnHomepage: false,
    createdAt: now,
    updatedAt: now,
  };

  const { data, error } = await supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .insert({
      ...review,
      createdAt: review.createdAt.toISOString(),
      updatedAt: review.updatedAt.toISOString(),
    })
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  return (data as any).id as string;
};

// Get a single review by ID
export const getReview = async (reviewId: string): Promise<Review | null> => {
  const { data, error } = await supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .select(REVIEW_COLUMNS_FULL)
    .eq("id", reviewId)
    .single();
  if (error || !data) return null;
  return {
    id: data.id,
    ...(convertTimestamps({
      ...data,
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      approvedAt: data.approvedAt ? new Date(data.approvedAt) : undefined,
    }) as any),
  } as Review;
};

// Get all reviews with optional filtering
export const getReviews = async (filters?: {
  status?: ReviewStatus;
  customerId?: string;
  featured?: boolean;
  displayOnHomepage?: boolean;
}): Promise<Review[]> => {
  let queryBuilder = supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .select(REVIEW_COLUMNS_FULL)
    .order("createdAt", { ascending: false });

  if (filters?.status) {
    queryBuilder = queryBuilder.eq("status", filters.status);
  }
  if (filters?.customerId) {
    queryBuilder = queryBuilder.eq("customerId", filters.customerId);
  }
  if (filters?.featured !== undefined) {
    queryBuilder = queryBuilder.eq("featured", filters.featured);
  }
  if (filters?.displayOnHomepage !== undefined) {
    queryBuilder = queryBuilder.eq("displayOnHomepage", filters.displayOnHomepage);
  }

  const { data, error } = await queryBuilder;
  if (error || !data) return [];
  return data.map(
    (row: any) =>
      ({
        id: row.id,
        ...(convertTimestamps({
          ...row,
          createdAt: new Date(row.createdAt),
          updatedAt: new Date(row.updatedAt),
          approvedAt: row.approvedAt ? new Date(row.approvedAt) : undefined,
        }) as any),
      }) as Review
  );
};

// Get approved reviews for homepage display
export const getApprovedReviews = async (limitCount?: number): Promise<Review[]> => {
  let queryBuilder = supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .select(REVIEW_HOMEPAGE_COLUMNS)
    .eq("status", "approved")
    .eq("displayOnHomepage", true)
    .order("order", { ascending: true })
    .order("createdAt", { ascending: false });
  if (limitCount) {
    queryBuilder = queryBuilder.limit(limitCount);
  }
  const { data, error } = await queryBuilder;
  if (error) {
    return [];
  }
  if (!data) return [];
  return data.map(
    (row: any) =>
      ({
        id: row.id,
        ...(convertTimestamps({
          ...row,
          createdAt: new Date(row.createdAt),
          updatedAt: new Date(row.updatedAt),
          approvedAt: row.approvedAt ? new Date(row.approvedAt) : undefined,
        }) as any),
      }) as Review
  );
};

// Get reviews for a specific customer
export const getCustomerReviews = async (customerId: string): Promise<Review[]> => {
  return getReviews({ customerId });
};

// Update review status (admin approval/rejection)
export const updateReviewStatus = async (
  reviewId: string,
  status: ReviewStatus,
  adminId: string,
  rejectedReason?: string
): Promise<void> => {
  const updateData: any = {
    status,
    updatedAt: new Date().toISOString(),
  };

  if (status === "approved") {
    updateData.approvedBy = adminId;
    updateData.approvedAt = new Date().toISOString();
  } else if (status === "rejected" && rejectedReason) {
    updateData.rejectedReason = rejectedReason;
  }

  const { error } = await supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .update(updateData)
    .eq("id", reviewId);
  if (error) throw new Error(error.message);
};

// Update review featured status
export const updateReviewFeatured = async (reviewId: string, featured: boolean): Promise<void> => {
  const { error } = await supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .update({ featured, updatedAt: new Date().toISOString() })
    .eq("id", reviewId);
  if (error) throw new Error(error.message);
};

// Update review homepage display status
export const updateReviewHomepageDisplay = async (
  reviewId: string,
  displayOnHomepage: boolean
): Promise<void> => {
  const { error } = await supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .update({
      displayOnHomepage,
      updatedAt: new Date().toISOString(),
    })
    .eq("id", reviewId);
  if (error) throw new Error(error.message);
};

export const updateReviewOrder = async (reviewId: string, order: number): Promise<void> => {
  const { error } = await supabase
    .from(SUPABASE_TABLES.REVIEWS)
    .update({ order, updatedAt: new Date().toISOString() })
    .eq("id", reviewId);
  if (error) throw new Error(error.message);
};

export const deleteReview = async (reviewId: string): Promise<void> => {
  const { error } = await supabase.from(SUPABASE_TABLES.REVIEWS).delete().eq("id", reviewId);
  if (error) throw new Error(error.message);
};

// Get review statistics
// Note: For production with large datasets, consider implementing server-side aggregation
// using Firebase Cloud Functions or caching these stats in a separate document
export const getReviewStats = async (): Promise<{
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  averageRating: number;
}> => {
  const { data, error } = await supabase.from(SUPABASE_TABLES.REVIEWS).select(REVIEW_STATS_COLUMNS);

  if (error || !data) {
    return {
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      averageRating: 0,
    };
  }

  const allReviews = (data as any[]).map((row) => ({
    status: row.status as ReviewStatus,
    rating: Number(row.rating ?? 0),
  }));

  const stats = {
    total: allReviews.length,
    pending: allReviews.filter((r) => r.status === "pending").length,
    approved: allReviews.filter((r) => r.status === "approved").length,
    rejected: allReviews.filter((r) => r.status === "rejected").length,
    averageRating: 0,
  };

  if (allReviews.length > 0) {
    const approvedReviews = allReviews.filter((r) => r.status === "approved");
    if (approvedReviews.length > 0) {
      stats.averageRating =
        approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length;
    }
  }

  return stats;
};
